import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { toast } from 'react-toastify';

// Define user role types
export type UserRole = 'requester' | 'provider' | 'admin';

// Define user data type
export interface UserData {
  uid: string;
  email: string;
  username: string;
  role: UserRole;
  createdAt: Date;
  isTestUser?: boolean;
}

// Define context type
interface AuthContextType {
  currentUser: User | null;
  userData: UserData | null;
  loading: boolean;
  register: (email: string, confirmEmail: string, password: string, username: string, role: UserRole) => Promise<void>;
  login: (email: string, password: string) => Promise<UserData | null>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data() as UserData);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Register new user
  async function register(email: string, confirmEmail: string, password: string, username: string, role: UserRole) {
    if (email !== confirmEmail) {
      throw new Error('Emails do not match');
    }
    
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if the email is the admin email
      const isAdmin = email.toLowerCase() === 'kphapps@gmail.com';
      
      // Create user document in Firestore
      const userData: UserData = {
        uid: user.uid,
        email: user.email || email,
        username,
        role: isAdmin ? 'admin' : role,
        createdAt: new Date(),
      };

      await setDoc(doc(db, 'users', user.uid), {
        ...userData,
        createdAt: serverTimestamp(),
      });
      setUserData(userData);
      toast.success('Registration successful!');
    } catch (error: any) {
      let message = 'Registration failed';
      if (error.code === 'auth/email-already-in-use') {
        message = 'This email is already registered';
      }
      toast.error(message);
      throw error;
    }
  }

  // Login
  async function login(email: string, password: string): Promise<UserData | null> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as UserData;
        setUserData(userData);
        toast.success('Login successful!');
        return userData;
      }
      
      toast.success('Login successful!');
      return null;
    } catch (error: any) {
      let message = 'Login failed';
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        message = 'Invalid email or password';
      }
      toast.error(message);
      throw error;
    }
  }

  // Logout
  async function logout() {
    try {
      await signOut(auth);
      setUserData(null);
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Error logging out');
      throw error;
    }
  }

  // Reset password
  async function resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent');
    } catch (error) {
      toast.error('Error sending password reset email');
      throw error;
    }
  }

  const value = {
    currentUser,
    userData,
    loading,
    register,
    login,
    logout,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}